import { TypedDocumentNode, gql } from '@apollo/client';
import classnames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Container } from '@sorare/core/src/atoms/layout/Container';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Section } from '../Section';
import { Description } from './Description';
import { Table } from './Table';
import { CompetitionDetailsGameplay_LeaderboardInterface } from './__generated__/index.graphql';

const messages = defineMessages({
  gameplay: {
    id: 'CompetitionDetails.Gameplay.Label',
    defaultMessage: 'Gameplay',
  },
  overview: {
    id: 'CompetitionDetails.Gameplay.Overview.Label',
    defaultMessage: 'Overview',
  },
});

const Wrapper = styled.div`
  display: flex;
  gap: var(--triple-unit);
  flex-direction: column;
  &.reverse-mobile {
    flex-direction: column-reverse;
  }
  @media ${tabletAndAbove} {
    &.reverse-mobile {
      flex-direction: column;
    }
    gap: var(--quadruple-unit);
  }
`;

type Props = {
  leaderboard?: CompetitionDetailsGameplay_LeaderboardInterface | null;
  nbCards: number;
  fullPage?: boolean;
  BudgetRequirements?: ReactNode;
  ScoringStrategy?: ReactNode;
};

export const Gameplay = ({
  leaderboard,
  nbCards,
  fullPage,
  BudgetRequirements,
  ScoringStrategy,
}: Props) => {
  const { descriptions } = leaderboard || {};
  const showOverview = !descriptions || descriptions.length > 0;
  return (
    <Container>
      <Wrapper className={classnames({ 'reverse-mobile': !fullPage })}>
        {showOverview && (
          <Section
            title={<FormattedMessage {...messages.overview} />}
            loading={!leaderboard}
          >
            <Description leaderboard={leaderboard} />
          </Section>
        )}
        <Section
          title={!fullPage && <FormattedMessage {...messages.gameplay} />}
          loading={!leaderboard}
        >
          <Table
            leaderboard={leaderboard}
            nbCards={nbCards}
            BudgetRequirements={BudgetRequirements}
            ScoringStrategy={ScoringStrategy}
          />
        </Section>
      </Wrapper>
    </Container>
  );
};

Gameplay.fragments = {
  LeaderboardInterface: gql`
    fragment CompetitionDetailsGameplay_LeaderboardInterface on LeaderboardInterface {
      slug
      descriptions
      ...CompetitionDetailsGameplayDescription_LeaderboardInterface
      ...CompetitionDetailsGameplayTable_LeaderboardInterface
    }
    ${Description.fragments.LeaderboardInterface}
    ${Table.fragments.LeaderboardInterface}
  ` as TypedDocumentNode<CompetitionDetailsGameplay_LeaderboardInterface>,
};
